import clsx from 'clsx'
import React, { AllHTMLAttributes, useEffect, useRef, useState } from 'react'

interface IRadio extends AllHTMLAttributes<HTMLInputElement> {
  label?: string
  orientation?: 'horizontal' | 'vertical'
  checkComponent?: React.ReactElement
  uncheckedComponent?: React.ReactElement
  customLabel?: React.ReactNode
}

function RadioButton({
  label,
  id,
  orientation = 'horizontal',
  checkComponent,
  uncheckedComponent,
  onChange,
  checked: passCheck,
  className,
  customLabel,
  disabled,
  ...rest
}: IRadio) {
  const [checked, setChecked] = useState(passCheck)
  const inputRef = useRef<HTMLInputElement>(null)

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(e.currentTarget.checked)
    onChange?.(e)
  }

  useEffect(() => {
    setChecked(passCheck)
  }, [passCheck])

  return (
    <div
      className={clsx(
        'flex relative text-jaa-shades-gray-3 text-base leading-[180%] font-medium hover:text-jaa-shades-black',
        orientation === 'horizontal'
          ? 'flex-row justify-start gap-2 items-center'
          : 'flex-col justify-start items-start',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <input
        type='radio'
        id={id}
        checked={checked}
        ref={inputRef}
        onChange={changeHandler}
        disabled={disabled}
        {...rest}
        className={clsx(
          'peer absolute opacity-0',
          !checkComponent &&
            `opacity-100 static appearance-none w-6 h-6 border-2 rounded-full border-jaa-blue flex justify-center items-center 
             after:block after:rounded-full after:w-3 after:h-3 after:m-[3px]
             checked:after:bg-jaa-blue checked:hover:bg-white checked:hover:border-2 checked:hover:border-point-bg-blue 
             focus-visible:ring-2 focus-visible:ring-jaa-blue focus-visible:ring-offset-2 focus-visible:ring-offset-white`
        )}
        aria-checked={checked}
      />

      <span
        onClick={() => inputRef.current?.click()}
        className={clsx('cursor-pointer', disabled && 'cursor-not-allowed')}
        aria-hidden='true'
      >
        {checked ? checkComponent : uncheckedComponent}
      </span>

      <label
        htmlFor={id}
        onClick={() => inputRef.current?.click()}
        className={clsx(
          'cursor-pointer peer-checked:text-jaa-text-black py-2',
          disabled && '!cursor-not-allowed'
        )}
      >
        {customLabel || label}
      </label>
    </div>
  )
}

export default RadioButton
